import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import PublicLayout from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

function Paras({ text }: { text: string }) {
  return (
    <>
      {text.split(/\n\n+/).map((para, i) => (
        <p key={i}>{para}</p>
      ))}
    </>
  );
}

function wordCount(post: (typeof blogPosts)[number]) {
  const parts = [
    post.content.introduction,
    post.content.conclusion,
    ...post.content.sections.map(s => `${s.title} ${s.content} ${(s.keyPoints || []).join(' ')}`),
  ];
  return parts.join(' ').split(/\s+/).filter(Boolean).length;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const sameCategory = blogPosts.filter(p => p.id !== post.id && p.category === post.category);
  const others = blogPosts.filter(p => p.id !== post.id && p.category !== post.category);
  const relatedPosts = [...sameCategory, ...others].slice(0, 3);

  const faqSection = post.content.sections.find(s =>
    /faq|questions fréquentes/i.test(s.title)
  );
  const faqEntities = (faqSection?.keyPoints || [])
    .map(point => {
      const [q, ...rest] = point.split(' — ');
      const a = rest.join(' — ').trim();
      if (!q || !a) return null;
      return {
        '@type': 'Question',
        name: q.replace(/^\d+\.\s*/, ''),
        acceptedAnswer: { '@type': 'Answer', text: a },
      };
    })
    .filter(Boolean);

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: 'https://clairify.fr/og-image.png',
    inLanguage: 'fr-FR',
    author: {
      '@type': 'Person',
      name: post.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Clairify',
      logo: {
        '@type': 'ImageObject',
        url: 'https://clairify.fr/logo-light.svg',
      },
    },
    datePublished: post.publishDate,
    dateModified: post.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://clairify.fr/blog/${post.slug}`,
    },
    keywords: post.keywords.join(', '),
    articleSection: post.category,
    wordCount: wordCount(post),
  };

  const faqStructuredData = faqEntities.length >= 2
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqEntities,
      }
    : null;

  return (
    <PublicLayout>
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.keywords}
        canonicalUrl={`https://clairify.fr/blog/${post.slug}`}
        publishDate={post.publishDate}
        author={post.author.name}
        ogType="article"
        structuredData={faqStructuredData ? [articleStructuredData, faqStructuredData] : articleStructuredData}
        markdownUrl={`https://clairify.fr/ai/blog/${post.slug}.md`}
      />

      <article className="article">
        <Link to="/blog" className="article-back">← Journal</Link>

        <div className="article-meta">
          <span>{post.category}</span>
          <time dateTime={post.publishDate}>
            {new Date(post.publishDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </time>
          <span>{post.readTime} min</span>
        </div>

        <h1>{post.title}</h1>
        <p className="lede">{post.excerpt}</p>

        <p className="text-sm text-theme-tertiary mb-10">
          {post.author.name} · {post.author.role}
        </p>

        <Paras text={post.content.introduction} />

        {post.content.sections.map((section, index) => (
          <section key={index}>
            <h2>{section.title}</h2>
            <Paras text={section.content} />

            {section.keyPoints && section.keyPoints.length > 0 && !/faq|questions fréquentes/i.test(section.title) && (
              <ul className="points">
                {section.keyPoints.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            )}

            {section.stats && section.stats.length > 0 && (
              <ul className="points">
                {section.stats.map((stat, idx) => (
                  <li key={idx}><strong className="text-theme-primary">{stat.value}</strong> — {stat.label}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <section>
          <h2>Conclusion</h2>
          <Paras text={post.content.conclusion} />
        </section>

        <div className="pub-cta" style={{ borderTop: '1px solid var(--theme-border)', marginTop: '3rem' }}>
          <p className="home-kicker">Studio</p>
          <h2>{post.content.cta.title}</h2>
          <p>{post.content.cta.description}</p>
          <Link to="/login">
            <Button size="lg">Essayer</Button>
          </Link>
        </div>

        {relatedPosts.length > 0 && (
          <section className="mt-16">
            <p className="home-kicker mb-6">À lire aussi</p>
            <div className="pub-grid" style={{ gridTemplateColumns: '1fr' }}>
              {relatedPosts.map(related => (
                <Link key={related.id} to={`/blog/${related.slug}`} className="pub-card">
                  <span>{related.category}</span>
                  <h3>{related.title}</h3>
                  <span className="meta">{related.readTime} min</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </PublicLayout>
  );
}
